import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import { gossipEvents } from "@/mocks/events";
import { gossipCategories } from "@/mocks/categories";
import EventCard from "@/components/feature/EventCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Reveal from "@/components/base/Reveal";

const PAGE_SIZE = 6;
const sortOptions = [
  { value: "latest", label: "最近整理" },
  { value: "heat", label: "按热度" },
  { value: "oldest", label: "最早收录" },
];

export default function Events() {
  const [params, setParams] = useSearchParams();

  const keyword = params.get("q") ?? "";
  const category = params.get("category") ?? "";
  const sort = params.get("sort") ?? "latest";
  const page = Math.max(1, Number(params.get("page") ?? "1") || 1);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    if (key !== "page") next.delete("page");
    setParams(next, { replace: false });
  };

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const list = gossipEvents.filter((event) => {
      const matchCategory = !category || event.category === category;
      const matchKeyword =
        !kw ||
        event.title.toLowerCase().includes(kw) ||
        event.summary.toLowerCase().includes(kw) ||
        event.tags.some((tag) => tag.toLowerCase().includes(kw));
      return matchCategory && matchKeyword;
    });

    return list.sort((a, b) => {
      if (sort === "heat") return b.heat - a.heat;
      if (sort === "oldest") return a.publishedAt.localeCompare(b.publishedAt);
      return b.updatedAt.localeCompare(a.updatedAt);
    });
  }, [keyword, category, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const canonical = `https://zhongjianshuyuan.cn/events${category ? `?category=${encodeURIComponent(category)}` : ""}`;

  useSeo({
    title: category
      ? `${category}吃瓜合集 - 网报吃瓜事件库列表`
      : "吃瓜事件库 - 网报吃瓜汇总、合集与盘点列表",
    description: category
      ? `网报吃瓜整理的${category}方向吃瓜合集与盘点，按事件串收录相关热门话题，打开即可按顺序追完每一个瓜的完整脉络。`
      : "网报吃瓜事件库收录近期热门瓜的汇总、合集与盘点列表，支持按分类、关键词与热度筛选，每个事件串都从发生整理到后续。",
    keywords: `${category ? `${category},` : ""}网报吃瓜,网报吃瓜汇总,网报吃瓜合集,网报吃瓜列表,吃瓜事件库`,
    canonical,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: category ? `${category}吃瓜合集` : "网报吃瓜事件库",
        url: canonical,
        inLanguage: "zh-CN",
        description: "按分类与热度整理的吃瓜事件串列表。",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "网报吃瓜", item: "https://zhongjianshuyuan.cn/" },
          { "@type": "ListItem", position: 2, name: "吃瓜事件库", item: "https://zhongjianshuyuan.cn/events" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "事件串列表",
        itemListElement: pageItems.map((event, index) => ({
          "@type": "ListItem",
          position: (currentPage - 1) * PAGE_SIZE + index + 1,
          name: event.title,
          url: `https://zhongjianshuyuan.cn/events/${event.id}`,
        })),
      },
    ],
  });

  return (
    <>
      <section className="w-full border-b border-background-200 bg-background-100 px-4 pb-10 pt-28 md:px-8 md:pb-12 md:pt-36">
        <Breadcrumb items={[{ label: "网报吃瓜", to: "/" }, { label: category ? category : "吃瓜事件库" }]} />
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
          {category ? `${category} · 吃瓜合集` : "吃瓜事件库"}
        </h1>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
          {category
            ? `这里收录 ${category} 方向的事件串，每一条都按发生、发酵、回应、后续四段整理，点进去从上往下读一遍即可追完。`
            : "所有事件串都在这里。可以按分类筛选、按关键词搜索，也可以切换热度排序，看看眼下哪些话题还在持续发酵。"}
        </p>
        <p className="mt-4 text-xs text-foreground-500">
          共收录 <strong className="font-semibold text-primary-600">{filtered.length}</strong> 条事件串
          {keyword && ` · 关键词「${keyword}」`}
        </p>
      </section>

      <section className="w-full px-4 py-10 md:px-8 md:py-12">
        <div className="flex flex-col gap-5 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex w-full items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 lg:max-w-md">
              <i className="ri-search-line text-foreground-500" />
              <input
                value={keyword}
                onChange={(e) => updateParam("q", e.target.value)}
                placeholder="搜事件标题、简介或话题标签"
                aria-label="搜索事件"
                className="w-full bg-transparent py-2.5 text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
              />
              {keyword && (
                <button
                  type="button"
                  aria-label="清空搜索"
                  onClick={() => updateParam("q", "")}
                  className="text-foreground-500 hover:text-primary-600"
                >
                  <i className="ri-close-circle-line" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <label htmlFor="sort-select" className="text-xs text-foreground-500 whitespace-nowrap">
                排序
              </label>
              <select
                id="sort-select"
                value={sort}
                onChange={(e) => updateParam("sort", e.target.value)}
                className="rounded-md border border-background-300 bg-background-100 px-3 py-2 text-sm text-foreground-800 outline-none focus:border-primary-400"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 border-t border-background-200 pt-4">
            <button
              type="button"
              onClick={() => updateParam("category", "")}
              className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors whitespace-nowrap ${
                !category ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              全部分类
            </button>
            {gossipCategories.map((cat) => (
              <button
                key={cat.slug}
                type="button"
                onClick={() => updateParam("category", cat.name)}
                className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors whitespace-nowrap ${
                  category === cat.name
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {pageItems.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {pageItems.map((event, index) => (
              <Reveal key={event.id} delay={index * 60} className="h-full">
                <EventCard event={event} />
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-20 text-center">
            <i className="ri-emotion-sad-line text-4xl text-foreground-400" />
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-900">没有匹配的事件串</h2>
            <p className="mt-2 text-sm text-foreground-600">换个关键词或切换分类试试，也可以直接浏览全部事件。</p>
            <button
              type="button"
              onClick={() => setParams(new URLSearchParams(), { replace: false })}
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
            >
              <i className="ri-refresh-line" />
              重置筛选条件
            </button>
          </div>
        )}

        {totalPages > 1 && (
          <nav aria-label="分页导航" className="mt-10 flex items-center justify-center gap-2">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => updateParam("page", String(currentPage - 1))}
              className="flex h-9 items-center gap-1 rounded-full border border-background-300 px-4 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:opacity-40 whitespace-nowrap"
            >
              <i className="ri-arrow-left-s-line" />
              上一页
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
              <button
                key={num}
                type="button"
                aria-current={num === currentPage ? "page" : undefined}
                onClick={() => updateParam("page", String(num))}
                className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-medium transition-colors ${
                  num === currentPage
                    ? "bg-primary-500 text-background-50"
                    : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                {num}
              </button>
            ))}
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => updateParam("page", String(currentPage + 1))}
              className="flex h-9 items-center gap-1 rounded-full border border-background-300 px-4 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:opacity-40 whitespace-nowrap"
            >
              下一页
              <i className="ri-arrow-right-s-line" />
            </button>
          </nav>
        )}

        <div className="mt-12 flex flex-col items-start justify-between gap-4 rounded-lg bg-accent-950 p-6 md:flex-row md:items-center">
          <div>
            <h2 className="font-heading text-lg font-bold text-white">没找到想追的那条瓜？</h2>
            <p className="mt-2 text-sm text-background-200">
              把你记得的时间点和关键词告诉我们，我们会去核对公开信息，补进事件库。
            </p>
          </div>
          <Link
            to="/about#contact"
            className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
          >
            <i className="ri-chat-forward-line" />
            去提交线索
          </Link>
        </div>
      </section>
    </>
  );
}